import { skipToken } from '@reduxjs/toolkit/query/react';
import {
  ANALYTICS_RESOURCE_PATHS,
  baseApi,
  losslessJsonResponseHandler,
  type AnalyticsFilter,
  type AnalyticsQuery,
  type AnalyticsResourceKey,
  type AnalyticsRowMap,
  type AnalyticsSchemaObjectDto,
  type PagedResult,
} from '@/shared/api';
import { formatFilter, toAnalyticsParams } from '../model/filters';
import { buildAnalyticsModel, type AnalyticsModel, type AnalyticsObject } from '../model/schema';

/** A row of any reporting object; values are primitives or null. */
export type AnalyticsRecord = Record<string, unknown>;

export interface AnalyticsPageArgs {
  /** API path from the schema, e.g. `/api/analytics/stripe/charges`. */
  path: string;
  query: AnalyticsQuery;
}

export interface AnalyticsGroupCountsArgs {
  /** The table or view as `schema.Name`, e.g. `stripe.Charges`. */
  object: string;
  column: string;
  /** `value`: the largest groups by value; `month`: every month (`yyyy-MM`) of a date column. */
  by: 'value' | 'month';
  /** Value groups returned (1–50); the total counts them all. */
  top?: number;
  filters: AnalyticsFilter[];
}

export interface AnalyticsGroupCounts {
  /** A null key groups the empty values; large integers arrive as exact strings. */
  groups: { key: string | number | boolean | null; count: number }[];
  /** Rows matching the filters. */
  total: number;
  /** Groups, including those beyond `top`. */
  groupCount: number;
}

/** The schema reflects the deployed backend; it changes only with a backend release. */
const SCHEMA_CACHE_SECONDS = 60 * 60;

export const analyticsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAnalyticsSchema: build.query<AnalyticsSchemaObjectDto[], undefined>({
      query: () => '/api/analytics/schema',
      keepUnusedDataFor: SCHEMA_CACHE_SECONDS,
    }),
    /** One endpoint for every table and view, mirroring the backend's generic AnalyticsService<T>. */
    getAnalyticsPage: build.query<PagedResult<AnalyticsRecord>, AnalyticsPageArgs>({
      query: ({ path, query }) => ({
        url: path,
        params: toAnalyticsParams(query),
        // bigint/decimal values that do not fit a JS number arrive as exact strings.
        responseHandler: losslessJsonResponseHandler,
      }),
      providesTags: (_result, _error, { path }) => [{ type: 'Analytics', id: path }],
    }),
    /** Rows per value or per month of one column: one grouped scan for a chart. */
    getAnalyticsGroupCounts: build.query<AnalyticsGroupCounts, AnalyticsGroupCountsArgs>({
      query: ({ filters, ...args }) => ({
        url: '/api/analytics/group-counts',
        params: { ...args, filter: filters.map(formatFilter) },
        responseHandler: losslessJsonResponseHandler,
      }),
    }),
  }),
});

const { useGetAnalyticsSchemaQuery, useGetAnalyticsPageQuery, useGetAnalyticsGroupCountsQuery } =
  analyticsApi;

export const useAnalyticsGroupCounts = useGetAnalyticsGroupCountsQuery;

// The model holds object graphs (relations), so it lives outside the store: one instance per
// schema response, shared by every component.
const models = new WeakMap<readonly AnalyticsSchemaObjectDto[], AnalyticsModel>();

function modelFor(schema: readonly AnalyticsSchemaObjectDto[]): AnalyticsModel {
  let model = models.get(schema);
  if (!model) {
    model = buildAnalyticsModel(schema);
    models.set(schema, model);
  }
  return model;
}

/** The reporting model (all tables, views, columns, entities). Loaded once per session. */
export function useAnalyticsModel() {
  const { data, error, isLoading, refetch } = useGetAnalyticsSchemaQuery(undefined);
  return { model: data ? modelFor(data) : undefined, error, isLoading, refetch };
}

/** Rows of any reporting object (generic pages). Pass `skipToken` to defer the request. */
export function useAnalyticsRecords(
  object: AnalyticsObject | undefined,
  query: AnalyticsQuery | typeof skipToken,
) {
  return useGetAnalyticsPageQuery(
    object && query !== skipToken ? { path: object.path, query } : skipToken,
  );
}

/** Typed rows of a known resource, e.g. `useAnalyticsQuery('stripe/customers', …)`. */
export function useAnalyticsQuery<Key extends AnalyticsResourceKey>(
  key: Key,
  query: AnalyticsQuery | typeof skipToken,
) {
  const result = useGetAnalyticsPageQuery(
    query === skipToken ? skipToken : { path: ANALYTICS_RESOURCE_PATHS[key], query },
  );
  // The endpoint is shared by all resources; the path determines the row type.
  return { ...result, data: result.data as PagedResult<AnalyticsRowMap[Key]> | undefined };
}
